<template>
  <div v-if="!isLoading && pendingInvitations.length">
    <hr class="border-[#3E4546] mb-4" />
    <h3 class="text-neutral-400 font-semibold text-base mb-2">
      Pending invitations
    </h3>
    <div class="flex flex-col gap-4 w-full mb-4">
      <div v-for="invitation in pendingInvitations" :key="invitation.id">
        <div class="flex items-center gap-4">
          <div
            class="w-full font-semibold text-base leading-5 text-neutral-50 flex gap-4"
          >
            <div
              class="size-[24px] border border-neutral-400 border-dashed rounded-full"
            />
            {{ invitation.email }}
          </div>
          <div class="md:min-w-36 hidden sm:block">
            <Select
              :model-value="invitation.metadata.role_id"
              :disabled="!hasPermission"
              @update:model-value="onInvitationSelect($event, invitation.id)"
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="viewer"> Viewer </SelectItem>
                <SelectItem value="editor"> Editor </SelectItem>
                <SelectSeparator />
                <SelectItem value="resend"> Resend Invite </SelectItem>
                <SelectItem value="remove"> Remove </SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { useStore } from 'vuex';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  SelectSeparator,
} from '@ui/components/select';
import { useProjectPendingInvitations } from '@/queries/composables.js';
import { useMutation } from '@tanstack/vue-query';
import requests from '@/vuex/requests.js';
import queryClient from '@/queries/queryClient.js';
import { queries } from '@/queries/index.js';
import { toast } from 'vue-sonner';
import { useProtect } from '@/composables/useProtect.js';
import { SYS_PERMISSIONS } from '@/constants.js';

const store = useStore();

const { hasPermission } = useProtect({
  permissions: [SYS_PERMISSIONS.SYS_MEMBERSHIPS_MANAGE],
});

const { data: pendingInvitations, isLoading } = useProjectPendingInvitations(
  store.getters['user/projects/getOpen']
);

const { mutate: resendInvitation } = useMutation({
  mutationFn: async id => {
    await requests.invitations.resend({ id });
  },
  onSuccess: async () => {
    await queryClient.invalidateQueries({
      queryKey: queries.project.pendingInvitations(
        store.getters['user/projects/getOpen']
      ).queryKey,
    });
    toast.success('Invitation resent');
  },
});

const { mutate: deleteInvitation } = useMutation({
  mutationFn: async id => {
    await requests.invitations.delete({ id });
  },
  onSuccess: async () => {
    await queryClient.invalidateQueries({
      queryKey: queries.project.pendingInvitations(
        store.getters['user/projects/getOpen']
      ).queryKey,
    });
  },
});

const { mutate: updateInvitation } = useMutation({
  mutationFn: async payload => {
    await requests.invitations.update({ ...payload });
  },
  onSuccess: async () => {
    await queryClient.invalidateQueries({
      queryKey: queries.project.pendingInvitations(
        store.getters['user/projects/getOpen']
      ).queryKey,
    });
    toast.success('Invitation updated');
  },
});

const onInvitationSelect = (value, invitationId) => {
  console.log(value, invitationId);
  switch (value) {
    case 'remove':
      deleteInvitation(invitationId);
      break;
    case 'resend':
      resendInvitation(invitationId);
      break;
    default:
      updateInvitation({ id: invitationId, roleId: value });
      break;
  }
};
</script>
